/* ================== (Mostly) dupilated content from AHA-DIALOG because Stencil cannot extend classes. Have only changed variable names to be `--aha-modal` vs `--aha-dialog`. ================== */
/*
  NOTE: While it's relatively easy to style a native <dialog> in Chrome/Safari (with a few caveats), Firefox is much much much more difficult.

  For example, if the <dialog> is ever positioned, suddenly CSS transforms that exist in any **parent element** of the <dialog> can actually affect the styling of the <dialog>, even though:

  1. The <dialog> element when opened in "modal" mode exists specifically to break out of parent rendering contexts and should prevent this from happening (so the behavior is likely a bug in Firefox).
  2. The <dialog> exists inside the shadow DOM of the <aha-dialog>, and as such styles like this should not be able to cross the shadow DOM barrier. Again, this should be impossible, but here we are.

  These transforms on any parent element can cause elements inside the <dialog> to be mis-aligned, scrolled off screen, invisible, or even break the ability to interact with them. In short, Firefox is a mess.

  Hopefully one day Firefox will fix these bugs, but for now, the styling compromises in this file are a necessary fact of life if we want to use the native <dialog> element.
 */

:host {
  --aha-modal--shadow: var(--theme-shadow-medium);
  --aha-modal--shadow-maximal: var(--theme-shadow-deep);
  --aha-modal--filter: saturate(0);
  --aha-modal--filter-maximal: saturate(1);
  --aha-modal--fade-duration: 0.2s;
  --aha-modal--fade-timing-function: cubic-bezier(0.39, 0.58, 0.57, 1);
  --aha-modal--fade-in-duration: var(--aha-modal--fade-duration);
  --aha-modal--fade-in-timing-function: var(--aha-modal--fade-timing-function);
  --aha-modal--fade-out-duration: 0.1s;
  --aha-modal--fade-out-timing-function: var(--aha-modal--fade-timing-function);
  --aha-modal--maximal-fade-duration: 0.1s;
  --aha-modal--loader-fade-duration: 0.1s;
  --aha-modal--background: var(--theme-primary-background);
  --aha-modal--color: var(--theme-primary-text);
  --aha-modal--border-radius: var(--Common--border-radius);

  --aha-modal--min-width: ;
  --aha-modal--width: ;
  --aha-modal--max-width: ;
  --aha-modal--min-height: ;
  --aha-modal--height: ;
  --aha-modal--max-height: ;

  z-index: -1;
  display: contents;
}
:host,
* {
  box-sizing: border-box;
}

dialog {
  padding: 0;
  z-index: 0;
  background: none;
  border: none;
  outline: none;
  height: 100vh;
  width: 100vw;
  margin: 0;
  max-width: 100%;
  max-height: 100%;
  color: var(--aha-modal--color);
}

dialog::backdrop {
  background: rgba(0, 0, 0, 0.4);
}

/* Ensure that only the topmost <dialog> which can have a backdrop (ie. does not have `hideBackdrop === true`) actually _has_ a backdrop. */
:host(:not([data-backdrop])) dialog::backdrop {
  display: none;
}

#boundary {
  width: 100%;
  height: 100%;
}

/* Center vertically and horizontally. */
:host([position='center']) #inner {
  inset-block: 0;
  inset-inline: 0;
}

/* Center horizontally. */
:host([position='h-center']) #inner {
  inset-inline: 0;
  top: 35px;
}

/* Center vertically. */
:host([position='v-center']) #inner {
  inset-block: 0;
}

:host(
    :is([position='center'], [position='h-center'], [position='v-center']):is(
        [style^='top'],
        [style^='bottom'],
        [style*=';top'],
        [style*=';bottom'],
        [style*=' top'],
        [style*=' bottom'],
        [style^='inset'],
        [style*=';inset'],
        [style*=' inset']
      )
  )
  #inner {
  border-radius: var(--aha-modal--border-radius);
  inset-block: auto;
  height: auto;
}

:host(
    :is([position='center'], [position='h-center'], [position='v-center']):is(
        [style^='left'],
        [style^='right'],
        [style*=';left'],
        [style*=';right'],
        [style*=' left'],
        [style*=' right'],
        [style^='inset'],
        [style*=';inset'],
        [style*=' inset']
      )
  )
  #inner {
  inset-inline: auto;
  width: auto;
}

#inner {
  display: inline-block;
  position: absolute;
  background: var(--aha-modal--background);
  min-width: var(--aha-modal--min-width);
  width: var(--aha-modal--width, fit-content);
  max-width: var(--aha-modal--max-width);
  min-height: var(--aha-modal--min-height);
  height: var(--aha-modal--height, fit-content);
  max-height: var(--aha-modal--max-height);
  box-shadow: var(--aha-modal--shadow);
  border-radius: var(--aha-modal--border-radius);
  filter: var(--aha-modal--filter);
  margin: auto;
  transition: box-shadow 0.2s linear;
}

#inner.closing {
  animation: var(--aha-modal--fade-out-duration) linear fadeOut forwards;
}
#inner.opening {
  animation: var(--aha-modal--fade-in-duration) linear fadeIn forwards;
}
:host([maximal]) #inner {
  box-shadow: var(--aha-modal--shadow-maximal);
  filter: var(--aha-modal--filter-maximal);
}

/* Loader specific CSS. */
#loader {
  display: none;
  position: absolute;
  inset: 0;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: opacity var(--aha-modal--loader-fade-duration) ease-in-out;
}
:host([loading]) #loader {
  opacity: 1;
  display: flex;
}
:host ::slotted(*) {
  transition: filter var(--aha-modal--maximal-fade-duration) ease-in-out;
}
:host([loading]) ::slotted(*) {
  filter: grayscale(1) opacity(0.5);
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes fadeOut {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
    display: none;
  }
}

@media (prefers-reduced-motion) {
  :host {
    --aha-modal--fade-in-duration: 0s;
    --aha-modal--fade-out-duration: 0s;
    --aha-modal--maximal-fade-duration: 0s;
    --aha-modal--loader-fade-duration: 0s;
  }
}

@media (prefers-contrast: more) {
  #inner {
    outline: var(--outline-focus-ring);
  }
}

/* ================== Things specific to AHA-MODAL (not shared with AHA-DIALOG). ================== */
:host {
  --aha-modal--min-width: ;
  --aha-modal--width: fit-content;
  --aha-modal--max-width: ;

  --aha-modal--width-x-small: 300px;
  --aha-modal--width-small: 400px;
  --aha-modal--width-medium: 800px;
  --aha-modal--width-large: 90vw;

  --aha-modal--max-height: 90vh;
  --aha-modal--max-width: 95vw;

  --aha-modal--dialog-opacity: 1;

  position: fixed;
  z-index: -1;
  display: contents;
  overflow: auto;
}

:host([size='x-small']) #inner {
  --aha-modal--width: var(--aha-modal--width-x-small);
}
:host([size='small']) #inner {
  --aha-modal--width: var(--aha-modal--width-small);
}
:host([size='medium']) #inner {
  --aha-modal--width: var(--aha-modal--width-medium);
}
:host([size='large']) #inner {
  --aha-modal--min-width: var(--aha-modal--width-medium);
  --aha-modal--width: var(--aha-modal--width-large);
}

:host([dragging]) {
  --aha-modal--dialog-opacity: 0.5;
}
:host([dragging]) {
  --aha-modal--background: transparent;
}
:host([dragging]) #inner ::slotted(*) {
  opacity: var(--aha-modal--dialog-opacity);
}
:host([dragging]) #inner::before {
  content: '';
  position: absolute;
  inset: -5px;
  border: 1px dashed var(--aha-blue-500);
}
